import { useCallback, useRef } from "react";

const MAX_HISTORY = 50;

/**
 * Hook para undo/redo do flow.
 * Armazena snapshots de { nodes, edges } num stack.
 *
 * Uso:
 *   const { pushSnapshot, undo, redo, canUndo, canRedo } = useFlowHistory();
 *   // Ao alterar nodes/edges:  pushSnapshot(nodes, edges);
 *   // Ctrl+Z:                  const prev = undo();  if(prev) { setNodes(prev.nodes); setEdges(prev.edges); }
 *   // Ctrl+Shift+Z:            const next = redo();  if(next) { setNodes(next.nodes); setEdges(next.edges); }
 */
export default function useFlowHistory() {
  const historyRef = useRef([]);
  const indexRef = useRef(-1);
  const skipNextRef = useRef(false);

  const pushSnapshot = useCallback((nodes, edges) => {
    if (skipNextRef.current) {
      skipNextRef.current = false;
      return;
    }
    const snapshot = {
      nodes: JSON.parse(JSON.stringify(nodes)),
      edges: JSON.parse(JSON.stringify(edges)),
    };
    const next = indexRef.current + 1;
    historyRef.current = historyRef.current.slice(0, next);
    historyRef.current.push(snapshot);
    if (historyRef.current.length > MAX_HISTORY) {
      historyRef.current.shift();
    } else {
      indexRef.current = next;
    }
  }, []);

  const undo = useCallback(() => {
    if (indexRef.current <= 0) return null;
    indexRef.current -= 1;
    skipNextRef.current = true;
    return historyRef.current[indexRef.current];
  }, []);

  const redo = useCallback(() => {
    if (indexRef.current >= historyRef.current.length - 1) return null;
    indexRef.current += 1;
    skipNextRef.current = true;
    return historyRef.current[indexRef.current];
  }, []);

  return {
    pushSnapshot,
    undo,
    redo,
    canUndo: indexRef.current > 0,
    canRedo: indexRef.current < (historyRef.current.length || 0) - 1,
  };
}
